(ns ui.stepper
  "Generic, Radix-style multi-step form primitives — free of any domain/form code.

   Composed like the other compound primitives in `ui/` (accordion, tabs): a
   `stepper-root` provides shared state through a React context, and the parts
   read from it. Built on `ui.tabs` (itself Radix Tabs) for the strip + panels.

     [stepper/stepper-root {:steps steps :ctx ctx}
      [stepper/stepper-list {:data-cy #(str \"step-\" (name %))}]
      [stepper/stepper-content {:value :details}  [details-body ctx]]
      [stepper/stepper-content {:value :schedule} [schedule-body ctx]]
      [stepper/stepper-footer
       [stepper/stepper-prev {:on-close g}]
       [stepper/stepper-next]
       [stepper/stepper-submit {:on-click f} \"Create\"]]]

   Step *bodies* are composed explicitly as `stepper-content` children (Radix
   `Tabs.Content` style) — the caller passes the rendered hiccup directly, so
   bodies get their `ctx` lexically rather than through context.

   A *step* is a plain metadata map (no body — that lives in `stepper-content`):

     {:key    :details            ; unique step id (keyword), required
      :label  \"Details\"           ; tab-strip label, required
      :valid?   (fn [ctx] bool)   ; optional — gates Next / actions (default true)
      :when     (fn [ctx] bool)   ; optional — include the step (default true)
      :skipped? (fn [ctx] bool)}  ; optional — shown but never completed or selectable

   `ctx` is opaque to the primitives — whatever map the caller passes is handed
   straight to every step fn. data-cy schemes are caller-supplied as
   `(fn [step-key] -> string)`."
  (:require ["lucide-react" :refer [ArrowLeftIcon]]
            ["react" :as react]
            [applied-science.js-interop :as jsi]
            [ui.button :as ui.button]
            [ui.dialog :as dialog]
            [ui.tabs :as ui.tabs]
            [ui.utils :refer [clsx separate-props]]))


;; -----------------------------------------------------------------------------
;; Pure step helpers (usable standalone)
;; -----------------------------------------------------------------------------

(defn visible-steps
  "Steps whose optional `:when` predicate (called with `ctx`) is truthy."
  [steps ctx]
  (vec (filter (fn [{pred :when}] (or (nil? pred) (pred ctx))) steps)))


(defn step-index
  "Index of the step with key `k` in `steps`, or 0 when not found."
  [steps k]
  (or (->> steps (keep-indexed (fn [i {:keys [key]}] (when (= key k) i))) first) 0))


(defn step-valid?
  "A step is valid when it declares no `:valid?`, or its `:valid?` (called with
   `ctx`) returns truthy."
  [{pred :valid?} ctx]
  (or (nil? pred) (boolean (pred ctx))))


;; -----------------------------------------------------------------------------
;; Shared state context
;; -----------------------------------------------------------------------------
;; The state map (with fns) is stashed under a single `:state` key of a JS object
;; so reagent's prop conversion leaves it untouched — same trick as dag-context.

(defonce ^:private step-context (react/createContext nil))


(defn- use-stepper []
  (jsi/get (react/useContext step-context) :state))


;; -----------------------------------------------------------------------------
;; Root — provides state, renders the underlying tabs-root
;; -----------------------------------------------------------------------------

(defn- root-inner [props & children]
  (let [{:keys [steps ctx initial-step value on-value-change class]} props
        [internal set-internal] (react/useState (or initial-step (:key (first steps))))
        controlled? (some? value)
        step        (if controlled? value internal)
        set-step    (fn [k]
                      (when-not controlled? (set-internal k))
                      (when on-value-change (on-value-change k)))
        state       {:steps steps :ctx ctx :step step :set-step set-step
                     :index (step-index steps step)}]
    [:> (jsi/get step-context :Provider) {:value #js {:state state}}
     (into [ui.tabs/tabs-root {:value           (name step)
                               :on-value-change #(set-step (keyword %))
                               :activation-mode "manual"
                               :class           class}]
           children)]))


(defn stepper-root
  "Owns the current step (uncontrolled, or controlled via `:value` +
   `:on-value-change`) and shares it with the parts via context. Options:

     :steps            vector of step maps (already filtered — see `visible-steps`)
     :ctx              value handed to each step's :body/:valid?
     :initial-step     starting step key (uncontrolled; default: first step)
     :value            current step key (controlled)
     :on-value-change  (fn [step-key]) — fired on every step change
     :class            class for the underlying tabs-root"
  [& args]
  (let [[props children] (separate-props args)]
    (into [:f> root-inner props] children)))


;; -----------------------------------------------------------------------------
;; List — the tab strip
;; -----------------------------------------------------------------------------

(defn- list-inner [{:keys [data-cy class navigable?]}]
  (let [{:keys [steps index ctx]} (use-stepper)]
    (into [ui.tabs/tabs-list {:variant :underlined :class class}]
          (for [[i {:keys [key label skipped?]}] (map-indexed vector steps)]
            (let [skipped?   (boolean (and skipped? (skipped? ctx)))
                  completed? (and (< i index) (not skipped?))]
              ^{:key (name key)}
              [ui.tabs/tabs-trigger
               {:value    (name key)
                :variant  :underlined
                :disabled (not (and navigable? completed?))
                :data-cy  (when data-cy (data-cy key))
                :class    (clsx {"opacity-100" (not skipped?)
                                 "text-kleene-green dark:text-kleene-green border-kleene-text-success" completed?})}
               label])))))


(defn stepper-list
  "Progress strip — a trigger per step, with completed steps coloured.
   Options:

     :data-cy     (fn [step-key] -> string) for each trigger
     :navigable?  when true, completed steps can be selected to go back
     :class       extra class for the strip"
  [& args]
  (let [[props _children] (separate-props args)]
    [:f> list-inner props]))


;; -----------------------------------------------------------------------------
;; Content — one panel per step, composed explicitly (Radix Tabs.Content style)
;; -----------------------------------------------------------------------------

(defn stepper-content
  "A single step's panel — shown when its step is active. Compose one per
   (visible) step as a child of `stepper-root`, passing the rendered body as
   children. Options:

     :value  step key (keyword) this panel belongs to — required
     :class  class for the panel (default \"pt-4\")"
  [& args]
  (let [[{:keys [value class]} children] (separate-props args)]
    (into [ui.tabs/tabs-content {:value (name value) :class (or class "pt-4")}]
          children)))


;; -----------------------------------------------------------------------------
;; Footer — a bottom-right action bar composed from the buttons below
;; -----------------------------------------------------------------------------

(defn stepper-footer
  "Sticky bottom-right action bar for a wizard flow — wraps
   `ui.dialog/dialog-footer` and pins it to the bottom of the scrolling body
   (`sticky bottom-0`), kept here so every stepper part composes from one ns.
   Compose `stepper-prev`, `stepper-next`, and `stepper-submit` as children;
   Next/Submit self-select on whether the current step is last."
  [& args]
  (let [[props children] (separate-props args)]
    (into [dialog/dialog-actions (update props :class #(clsx "sticky bottom-0 border-t bg-kleene-background py-4" %))]
          children)))


;; -----------------------------------------------------------------------------
;; Footer buttons — composable Back / Next / Submit, each reads context
;; -----------------------------------------------------------------------------

(defn- prev-inner [{:keys [on-close cancel-label] :as props}]
  (let [{:keys [steps set-step index]} (use-stepper)
        first? (zero? index)]
    [ui.button/button
     (merge {:variant  :empty-transparent
             :icon     (when-not first? ArrowLeftIcon)
             :on-click (if first? on-close #(set-step (:key (nth steps (dec index)))))}
            (dissoc props :on-close :cancel-label))
     (if first? (or cancel-label "Cancel") "Back")]))


(defn stepper-prev
  "Back button: navigates to the previous step, or fires `:on-close` on the
   first step (acting as Cancel). `:cancel-label` overrides the first-step label;
   remaining props pass through to `ui.button/button`."
  [& args]
  (let [[props _children] (separate-props args)]
    [:f> prev-inner props]))


(defn- next-inner [props]
  (let [{:keys [steps set-step index ctx]} (use-stepper)
        last?   (= index (dec (count steps)))
        current (nth steps index)]
    (when-not last?
      [ui.button/button
       (merge {:variant  :primary
               :disabled (not (step-valid? current ctx))
               :on-click #(set-step (:key (nth steps (inc index))))}
              props)
       "Next"])))


(defn stepper-next
  "Next button: advances to the next step, disabled when the current step's
   `:valid?` fails. Renders nothing on the last step. Props pass through to
   `ui.button/button`."
  [& args]
  (let [[props _children] (separate-props args)]
    [:f> next-inner props]))


(defn- submit-inner [props & children]
  (let [{:keys [steps index]} (use-stepper)
        last? (= index (dec (count steps)))]
    (when last?
      (into [ui.button/button (merge {:variant :primary} props)]
            (or (seq children) ["Submit"])))))


(defn stepper-submit
  "Submit button, rendered only on the final step. Props pass straight to
   `ui.button/button` (e.g. `:on-click`, `:data-cy`); children are the label
   (default \"Submit\")."
  [& args]
  (let [[props children] (separate-props args)]
    (into [:f> submit-inner props] children)))
